import React, { useEffect, useState } from "react";
import { Button, Table, notification, Input, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";

import NewDiscountModal from "./NewDiscountModal";
import axiosInstance from "../../../utilities/axios";
import DiscountModal from "./DiscountModal";
import PrintButton from "./PrintButton";

const { Text } = Typography;

const getBasePath = (pathname) => {
  const pathSegments = pathname.split("/");
  if (pathSegments[2] === undefined) {
    return "/";
  } else {
    return `/${pathSegments[2]}`;
  }
};

const Discounts = () => {
  const location = useLocation();

  const selectedKey = getBasePath(location.pathname);

  const [discounts, setDiscounts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const [openNewCodeModal, setOpenNewCodeModal] = useState(false);

  const [endDateFilter, setEndDateFilter] = useState(["active"]);

  useEffect(() => {
    setSearch("");
  }, [selectedKey]);

  useEffect(() => {
    setLoading(true);
    const types =
      selectedKey === "/discount-reasons"
        ? ["marketing"]
        : selectedKey === "/subsidies"
          ? ["subsidy"]
          : selectedKey === "/matrix"
            ? ["matrix"]
            : selectedKey === "/affinity-partners"
              ? ["affinity"]
              : ["everyday_low"];
    axiosInstance
      .get("/discounts", {
        params: {
          search,
          types: types,
        },
      })
      .then((response) => {
        setDiscounts(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [search, selectedKey]);

  const columns = [
    {
      title:
        selectedKey === "/discount-reasons"
          ? "Code"
          : selectedKey === "/subsidies"
            ? "Subsidy"
            : selectedKey === "/matrix"
              ? "Matrix"
              : selectedKey === "/everyday-low"
                ? "Everyday Low"
                : "Affinity Partner",
      dataIndex: "discountName",
      key: "discountName",
      sorter: (a, b) => a.discountName.localeCompare(b.discountName),
    },
    {
      title: "Effective Date",
      dataIndex: "effectiveDate",
      key: "effectiveDate",
    },
    {
      title: "Expiration Date",
      dataIndex: "expirationDate",
      key: "expirationDate",
      filters: [
        { text: "Expired", value: "expired" },
        { text: "Active", value: "active" },
      ],
      filteredValue: endDateFilter,
      onFilter: (value, record) => {
        if (value === "expired") {
          return record.expirationDate < new Date().toISOString();
        } else {
          // return where records are not expired or have no expiration date
          return (
            record.expirationDate >= new Date().toISOString() ||
            !record.expirationDate
          );
        }
      },
      render: (text, record) =>
        record.expirationDate ? record.expirationDate : "No expiration date",
    },
    {
      title: "Details",
      dataIndex: "",
      key: "x",
      width: 100,
      render: (text, record) => (
        <Button
          onClick={() => {
            setSelectedDiscount(record);
            setOpenModal(true);
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "32px" }}>
      <div style={{ display: "flex", gap: "8px" }}>
        <Input.Search
          placeholder={
            selectedKey === "/discount-reasons"
              ? "Search Discount Reasons"
              : selectedKey === "/subsidies"
                ? "Search Subsidies"
                : selectedKey === "/matrix"
                  ? "Search Matrix Discounts"
                  : selectedKey === "/everyday-low"
                    ? "Search Everyday Low Discounts"
                    : "Search Affinity Partners"
          }
          allowClear
          size="large"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <Button
          type="primary"
          size="large"
          onClick={() => setOpenNewCodeModal(true)}
          icon={<PlusOutlined />}
        >
          {selectedKey === "/discount-reasons"
            ? "Add Discount Reason"
            : selectedKey === "/subsidies"
              ? "Add Subsidy"
              : selectedKey === "/matrix"
                ? "Add Matrix Discount"
                : selectedKey === "/everyday-low"
                  ? "Add Everyday Low Discount"
                  : "Add Affinity Partner"}
        </Button>
        <PrintButton
          search={search}
          endDateFilter={endDateFilter}
          discountType={selectedKey === "/discount-reasons"
            ? "marketing"
            : selectedKey === "/subsidies"
              ? "subsidy"
              : selectedKey === "/matrix"
                ? "matrix"
                : selectedKey === "/everyday-low"
                  ? "everyday_low"
                  : "affinity"}
        />
      </div>
      <div style={{ marginTop: "16px" }}>
        <Table
          key={selectedKey}
          loading={loading}
          bordered
          dataSource={discounts}
          columns={columns}
          rowKey="id"
          onChange={(pagination, filters) => {
            setEndDateFilter(filters.expirationDate || []);
          }}
        />
      </div>
      <NewDiscountModal
        allowMultiple={
          selectedKey === "/matrix" ||
          selectedKey === "/discount-reasons" ||
          selectedKey === "/affinity-partners" ||
          selectedKey === "/everyday-low" ||
          selectedKey === "/subsidies"
        }
        visible={openNewCodeModal}
        onClose={() => setOpenNewCodeModal(false)}
        path={selectedKey}
        onChange={() => {
          axiosInstance
            .get("/discounts", {
              params: {
                search: search,
                types:
                  selectedKey === "/discount-reasons"
                    ? ["marketing"]
                    : selectedKey === "/subsidies"
                      ? ["subsidy"]
                      : selectedKey === "/matrix"
                        ? ["matrix"]
                        : selectedKey === "/everyday-low"
                          ? ["everyday_low"]
                          : ["affinity"],
              },
            })
            .then((response) => {
              setDiscounts(response.data);
            })
            .catch((error) => {
              notification.error({
                message: "Error",
                description: error.message,
              });
            });
        }}
      />
      <DiscountModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedDiscount(null);
        }}
        discount={selectedDiscount}
        path={selectedKey}
        onChange={() => {
          const types =
            selectedKey === "/discount-reasons"
              ? ["marketing"]
              : selectedKey === "/subsidies"
                ? ["subsidy"]
                : selectedKey === "/matrix"
                  ? ["matrix"]
                  : selectedKey === "/everyday-low"
                    ? ["everyday_low"]
                    : ["affinity"];
          axiosInstance
            .get("/discounts", {
              params: {
                search,
                types: types,
              },
            })
            .then((response) => {
              setDiscounts(response.data);
            })
            .catch((error) => {
              notification.error({
                message: "Error",
                description: error.message,
              });
            });
          setSelectedDiscount(null);
        }}
      />
    </div>
  );
};

export default Discounts;
